import { useTranslation } from 'react-i18next';
import type { UnifiedSelectCellResolution } from '../lib/unified-select-types';
import { UnifiedSelectOptionDialog } from './unified-select-option-dialog';
import { UnifiedSelectReserveConfirmDialog } from './unified-select-reserve-confirm-dialog';
import type { useUnifiedSelectReserve } from './use-unified-select-reserve';

interface ReserveDialogsProps {
  reserve: ReturnType<typeof useUnifiedSelectReserve>;
  freeCells: UnifiedSelectCellResolution[];
  heldByOtherCount: number;
  ownHeldCount: number;
  disabled: boolean;
}

export function ReserveDialogs({
  reserve,
  freeCells,
  heldByOtherCount,
  ownHeldCount,
  disabled,
}: ReserveDialogsProps) {
  const { t } = useTranslation('inventory');
  // OPTION over an existing hold is never permitted by the displacement matrix, whoever holds
  // it, so held-by-other and own-held cells are both excluded from option coverage.
  const optionExcludedCount = heldByOtherCount + ownHeldCount;

  return (
    <>
      {reserve.optionDialogOpen ? (
        <UnifiedSelectOptionDialog
          freeCellDates={freeCells.map((cell) => cell.dateFrom)}
          excludedSummaryText={
            optionExcludedCount > 0
              ? t('availability.select.optionDialog.excludedSummary', {
                  count: optionExcludedCount,
                })
              : undefined
          }
          labels={{
            title: t('availability.select.optionDialog.title'),
            modeDays: t('availability.select.optionDialog.modeDays'),
            modeDate: t('availability.select.optionDialog.modeDate'),
            confirmBtn: t('availability.select.optionDialog.confirmBtn'),
            cancelBtn: t('availability.select.optionDialog.cancelBtn'),
          }}
          disabled={disabled}
          onConfirm={reserve.confirmOption}
          onClose={reserve.closeOptionDialog}
        />
      ) : null}

      {reserve.reserveDialogOpen ? (
        <UnifiedSelectReserveConfirmDialog
          cells={freeCells}
          previewCells={reserve.previewTtl.data?.cells}
          previewPending={reserve.previewTtl.isPending}
          splitSummaryText={
            heldByOtherCount > 0
              ? t('availability.select.reserveDialog.splitSummary', {
                  reservedCount: freeCells.length,
                  queuedCount: heldByOtherCount,
                })
              : undefined
          }
          labels={{
            title: t('availability.select.reserveDialog.title'),
            dateRangeLabel: t('availability.select.reserveDialog.dateRangeLabel'),
            dateLabel: t('availability.select.reserveDialog.dateLabel'),
            datePlaceholder: t('availability.select.reserveDialog.datePlaceholder'),
            autoHint: t('availability.select.reserveDialog.autoHint'),
            description: t('availability.select.reserveDialog.description'),
            confirmBtn: t('availability.select.reserveDialog.confirmBtn'),
            cancelBtn: t('availability.select.reserveDialog.cancelBtn'),
          }}
          disabled={disabled}
          onConfirm={reserve.confirmReserve}
          onClose={reserve.closeReserveDialog}
        />
      ) : null}
    </>
  );
}
